import {
  Box,
  BoxProps,
  CloseButton,
  Flex,
  Heading,
  useColorModeValue,
} from '@chakra-ui/react';
import { ReactNode, useRef } from 'react';
import { useDrawer } from './DrawerContext';

interface DrawerProps extends BoxProps {
  title?: string;
  children?: ReactNode;
}

export function Drawer({ title, children, ...rest }: DrawerProps) {
  const {
    isDrawerOpen,
    closeDrawer,
    drawerWidth,
    setDrawerWidth,
    setIsResizing,
  } = useDrawer();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const resizerColor = useColorModeValue('gray.200', 'gray.700');
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = drawerRef.current?.clientWidth || 0;

    const minWidth = 220;
    const maxWidth = 600;

    // Set resizing state to true when starting to resize
    setIsResizing(true);

    let newWidth = startWidth;

    const doResize = (mouseMoveEvent: MouseEvent) => {
      newWidth = Math.min(
        Math.max(startWidth - (mouseMoveEvent.clientX - startX), minWidth),
        maxWidth
      );

      // Update context state during resize for real-time layout updates
      setDrawerWidth(newWidth);

      if (drawerRef.current) {
        drawerRef.current.style.width = `${newWidth}px`;
      }
    };

    const stopResize = () => {
      // Set resizing state to false when done resizing
      setIsResizing(false);
      window.removeEventListener('mousemove', doResize);
      window.removeEventListener('mouseup', stopResize);
    };

    window.addEventListener('mousemove', doResize);
    window.addEventListener('mouseup', stopResize);
  };

  return (
    <Box
      ref={drawerRef}
      position="fixed"
      height="100vh"
      top={0}
      right={0}
      width={`${drawerWidth}px`}
      transform={isDrawerOpen ? 'translateX(0)' : 'translateX(100%)'}
      transition="transform 0.3s ease"
      zIndex={3}
      bg={bgColor}
      borderLeft="1px"
      borderColor={borderColor}
      paddingTop="68px" // Header height
      boxShadow={isDrawerOpen ? '0 0 10px rgba(0,0,0,0.1)' : 'none'}
      {...rest}
    >
      <Box
        position="absolute"
        left={-2}
        top={0}
        height="100%"
        width="8px"
        cursor="ew-resize"
        zIndex={4}
        onMouseDown={handleMouseDown}
        _active={{
          backgroundColor: resizerColor,
        }}
      />

      <Flex
        paddingTop={2}
        paddingX={4}
        alignItems="center"
        justifyContent="space-between"
      >
        {title && <Heading size="xs">{title}</Heading>}
        <CloseButton onClick={closeDrawer} />
      </Flex>
      <Box
        overflowY="auto"
        height="calc(100vh - 108px)"
        paddingX={2}
        paddingBottom={2}
        fontSize="xs"
      >
        {children}
      </Box>
    </Box>
  );
}
